/**
 * Thin settings registry (Phase 5 scaffold).
 * Typed seats for toggle / min-max / enum / feature-flag — wiring payoff later.
 * Not a full options product; values are in-memory defaults until persistence GO.
 */

export type SettingKind = "toggle" | "minmax" | "enum" | "flag";

export type SettingDef =
  | {
      id: string;
      label: string;
      kind: "toggle";
      default: boolean;
    }
  | {
      id: string;
      label: string;
      kind: "minmax";
      min: number;
      max: number;
      step: number;
      default: number;
    }
  | {
      id: string;
      label: string;
      kind: "enum";
      options: readonly string[];
      default: string;
    }
  | {
      id: string;
      label: string;
      kind: "flag";
      default: boolean;
      note?: string;
    };

/** Registry seats — expand in place; do not invent CK-depth panels here. */
export const SETTINGS_REGISTRY: readonly SettingDef[] = [
  { id: "seasonTint", label: "Season tint", kind: "toggle", default: true },
  {
    id: "reduceMotion",
    label: "Reduce motion",
    kind: "enum",
    options: ["system", "on", "off"] as const,
    default: "system",
  },
  {
    id: "confirmEndTurn",
    label: "Confirm end turn",
    kind: "toggle",
    default: false,
  },
  {
    id: "mapEdgeFade",
    label: "Map edge soft fade",
    kind: "toggle",
    default: true,
  },
  {
    id: "feature.webglBoard",
    label: "WebGL board",
    kind: "flag",
    default: true,
    note: "P5 raw WebGL map (2D canvas fallback if GL unavailable)",
  },
  {
    id: "feature.mapCamera",
    label: "Map pan / zoom",
    kind: "flag",
    default: true,
    note: "Light C1 camera on WebGL board (wheel zoom, drag pan, dblclick reset)",
  },
  {
    id: "feature.mapRelief",
    label: "Terrain relief",
    kind: "flag",
    default: true,
    note: "Contour/elev shading on hex mesh (sticker-pack avoidance)",
  },
] as const;

export type SettingsValues = Record<string, boolean | number | string>;

export function defaultSettings(): SettingsValues {
  const out: SettingsValues = {};
  for (const s of SETTINGS_REGISTRY) {
    out[s.id] = s.default;
  }
  return out;
}

/** Local smoke: round-trip defaults through JSON (save/load hook shape). */
export function serializeSettings(v: SettingsValues): string {
  return JSON.stringify({ schema: 1, values: v });
}

export function parseSettings(raw: string): SettingsValues | null {
  try {
    const data = JSON.parse(raw) as { schema?: number; values?: SettingsValues };
    if (!data || data.schema !== 1 || !data.values || typeof data.values !== "object") {
      return null;
    }
    const base = defaultSettings();
    for (const s of SETTINGS_REGISTRY) {
      if (s.id in data.values) base[s.id] = data.values[s.id]!;
    }
    return base;
  } catch {
    return null;
  }
}
